 import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import baseUrl from "../api/utils";
import "../assets/css/invoicestyle.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { getPermission } from "../utils/permissionUtils";
import MODULES from "../utils/modules";

const fmt = (n) =>
  (n || n === 0)
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const ViewPurchaseBills = () => {
  const token = sessionStorage.getItem("token");
  const MySwal = withReactContent(Swal);
  const canCreate = getPermission(MODULES.PURCHASE_BILL, "canCreate");
  const canEdit   = getPermission(MODULES.PURCHASE_BILL, "canEdit");
  const canDelete = getPermission(MODULES.PURCHASE_BILL, "canDelete");
  const navigate = useNavigate();

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [filterUser, setFilterUser] = useState("All Users");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [filterMonth, setFilterMonth] = useState("All Purchase Bills");
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dateTo, setDateTo] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const [showCalendar, setShowCalendar] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const monthRef = useRef(null);
  const calendarRef = useRef(null);

  const monthOptions = ["All Purchase Bills", "This Month", "Last Month", "This Year"];

  const [totalPurchase, setTotalPurchase] = useState(0);
  const [paidTotal, setPaidTotal] = useState(0);
  const [unpaidTotal, setUnpaidTotal] = useState(0);
  const [overdueTotal, setOverdueTotal] = useState(0);

  // ── VIEW MODAL STATE ─────────────────────────────────
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewBill, setViewBill] = useState(null);
  const [viewItems, setViewItems] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // ── FETCH PURCHASE BILLS ─────────────────────────────
  const fetchBills = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (filterMonth === "All Purchase Bills") {
        params.append("fromDate", "2000-01-01");
        params.append("toDate", "2099-12-31");
      } else {
        params.append("fromDate", dateFrom.toISOString().split("T")[0]);
        params.append("toDate", dateTo.toISOString().split("T")[0]);
      }

      if (filterUser !== "All Users") {
        params.append("createdBy", filterUser);
      }

      const res = await axios.get(`${baseUrl}/getPurchaseBillsFiltered?${params.toString()}`, {
        headers: { Authorization: token },
      });

      const data = res.status === 200 ? res.data : [];
      data.sort((a, b) => new Date(b.billDate) - new Date(a.billDate));

      setBills(data);

      const total = data.reduce((sum, b) => sum + (b.grandTotal || 0), 0);
      const paid = data.reduce((sum, b) => sum + (b.amountPaid || 0), 0);

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      let overdue = 0;
      data.forEach((b) => {
        if (b.balanceAmount > 0 && b.dueDate) {
          const due = new Date(b.dueDate);
          due.setHours(0, 0, 0, 0);
          if (due < todayStart) overdue += b.balanceAmount;
        }
      });

      const balanceTotal = data.reduce((sum, b) => sum + (b.balanceAmount || 0), 0);
      const unpaid = balanceTotal - overdue;

      setTotalPurchase(total);
      setPaidTotal(paid);
      setOverdueTotal(overdue);
      setUnpaidTotal(unpaid);
    } catch (error) {
      console.error("Error fetching purchase bills:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBills(); }, [filterMonth, dateFrom, dateTo, filterUser]);

  const filteredBills = bills.filter((b) => {
    const q = searchQuery.toLowerCase();
    return (
      b.supplierName?.toLowerCase().includes(q) ||
      b.supplierBillNumber?.toLowerCase().includes(q)
    );
  });

  const userOptions = [
    { label: "All Users", initials: "AU", bg: "#e8f0fe", color: "#1a73e8" },
    { label: "Admin",     initials: "AD", bg: "#fce8e6", color: "#d93025" },
    { label: "Cashier",   initials: "CA", bg: "#e6f4ea", color: "#137333" },
  ];
  const selected = userOptions.find((u) => u.label === filterUser);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (monthRef.current && !monthRef.current.contains(e.target))
        setMonthDropdownOpen(false);
      if (calendarRef.current && !calendarRef.current.contains(e.target))
        setShowCalendar(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applyDateRange = (option) => {
    const today = new Date();
    const year  = today.getFullYear();
    const month = today.getMonth();
    if (option === "This Month") {
      setDateFrom(new Date(year, month, 1));
      setDateTo(new Date(year, month + 1, 0));
      setShowCalendar(false);
    } else if (option === "Last Month") {
      setDateFrom(new Date(year, month - 1, 1));
      setDateTo(new Date(year, month, 0));
      setShowCalendar(false);
    } else if (option === "This Year") {
      setDateFrom(new Date(year, 0, 1));
      setDateTo(new Date(year, 11, 31));
      setShowCalendar(false);
    } else if (option === "All Purchase Bills") {
      setShowCalendar(true);
    }
  };

  const handleMonthSelect = (option) => {
    setFilterMonth(option);
    applyDateRange(option);
    setMonthDropdownOpen(false);
  };

  const formatDate = (date) =>
    date.toLocaleDateString("en-GB").replace(/\//g, "/");

  // ── DUE DATE + relative countdown helpers ──────────────
  const getDueDateInfo = (dueDateStr, hasBalance) => {
    if (!dueDateStr) return null;
    const due = new Date(dueDateStr);
    if (isNaN(due.getTime())) return null;

    const dateLabel = due.toLocaleDateString("en-GB");

    if (!hasBalance) {
      return { dateLabel, subText: "", color: "#555" };
    }

    const startOfDay = (d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };
    const todayStart = startOfDay(new Date());
    const dueStart = startOfDay(due);
    const diffDays = Math.round((dueStart - todayStart) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        dateLabel,
        subText: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"}`,
        color: "#d93025",
      };
    }
    if (diffDays === 0) {
      return { dateLabel, subText: "Due today", color: "#e65100" };
    }
    if (diffDays === 1) {
      return { dateLabel, subText: "Due tomorrow", color: "#e65100" };
    }
    return { dateLabel, subText: `${diffDays} days left`, color: "#888" };
  };

  const handleDelete = async (id) => {
    const result = await MySwal.fire({
      title: "Delete Purchase Bill?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${baseUrl}/deletePurchaseBill/${id}`, {
          headers: { Authorization: token },
        });
        MySwal.fire("Deleted!", "Purchase bill deleted successfully.", "success");
        fetchBills();
      } catch (error) {
        MySwal.fire("Error", "Failed to delete purchase bill.", "error");
      }
    }
  };

  const handleView = async (id) => {
    setShowViewModal(true);
    setViewLoading(true);
    try {
      const [billRes, itemsRes] = await Promise.all([
        axios.get(`${baseUrl}/getPurchaseBill/${id}`, { headers: { Authorization: token } }),
        axios.get(`${baseUrl}/getPurchaseBillItems/${id}`, { headers: { Authorization: token } }),
      ]);
      setViewBill(billRes.data);
      setViewItems(itemsRes.data);
    } catch (error) {
      MySwal.fire("Error", "Failed to load purchase bill.", "error");
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div>
      <div className="invoice-card-wrapper" style={{ marginTop: "-115px" }}>
        <div>
          <div className="card invoice-card">
            <div className="card-body p-0">

              <div
                className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                style={{ gap: "12px", flexWrap: "wrap" }}
              >
                <div className="input-group input-group-sm" style={{ maxWidth: "300px" }}>
                  <div className="input-group-prepend">
                    <span className="input-group-text bg-white border-right-0">
                      <i className="fa-solid fa-magnifying-glass" style={{ color: "#aaa", fontSize: "13px" }}></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control border-left-0"
                    placeholder="Search Transactions"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ boxShadow: "none", fontSize: "13px" }}
                  />
                </div>
                <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "18px", color: "#555" }}>
                  <i className="fa-solid fa-xmark"></i>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                <h5 className="mb-0 font-weight-bold" style={{ fontSize: "16px" }}>Purchase Bills</h5>
                {canCreate && (
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => navigate("/addpurchase")}
                      style={{ fontWeight: "600", borderRadius: "6px", padding: "5px 16px" }}
                    >
                      <i className="fa-solid fa-plus mr-1"></i> Add Purchase
                    </button>
                  </div>
                )}
              </div>

              <div
                className="d-flex align-items-center px-3 py-2 border-bottom"
                style={{ background: "#fafafa", gap: "10px", flexWrap: "wrap" }}
              >
                <span style={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>Filter by :</span>

                <div ref={monthRef} style={{ position: "relative" }}>
                  <div
                    className="d-flex align-items-center"
                    onClick={() => setMonthDropdownOpen((p) => !p)}
                    style={{ border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: "pointer", gap: "6px", fontSize: "13px" }}
                  >
                    <span>{filterMonth}</span>
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: "10px", color: "#888", transition: "transform 0.2s", transform: monthDropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}></i>
                  </div>
                  {monthDropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 200, padding: "6px 0" }}>
                      {monthOptions.map((opt) => (
                        <div
                          key={opt}
                          onClick={() => handleMonthSelect(opt)}
                          style={{ padding: "8px 16px", fontSize: 13, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: filterMonth === opt ? "#f5f5f5" : "transparent" }}
                        >
                          <span>{opt}</span>
                          {filterMonth === opt && <span style={{ color: "#1a73e8", fontSize: 13 }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div ref={calendarRef} style={{ position: "relative" }}>
                  <div
                    className="d-flex align-items-center"
                    onClick={() => { if (filterMonth === "All Purchase Bills") setShowCalendar((p) => !p); }}
                    style={{ border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: filterMonth === "All Purchase Bills" ? "pointer" : "default", gap: "6px", fontSize: "13px" }}
                  >
                    <i className="fa-regular fa-calendar" style={{ color: "#888", fontSize: "12px" }}></i>
                    <span>{formatDate(dateFrom)}</span>
                    <span style={{ color: "#aaa" }}>To</span>
                    <span>{formatDate(dateTo)}</span>
                  </div>
                  {showCalendar && filterMonth === "All Purchase Bills" && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Select date range</div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 12, color: "#555", minWidth: 30 }}>From</span>
                        <DatePicker selected={dateFrom} onChange={(date) => setDateFrom(date)} selectsStart startDate={dateFrom} endDate={dateTo} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" />
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 12, color: "#555", minWidth: 30 }}>To</span>
                        <DatePicker selected={dateTo} onChange={(date) => setDateTo(date)} selectsEnd startDate={dateFrom} endDate={dateTo} minDate={dateFrom} dateFormat="dd/MM/yyyy" className="form-control form-control-sm" />
                      </div>
                    </div>
                  )}
                </div>

                <div ref={dropdownRef} style={{ position: "relative" }}>
                  <div
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", border: "1px solid #ddd", borderRadius: "20px", padding: "3px 12px", background: "#fff", cursor: "pointer", fontSize: "13px", userSelect: "none" }}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: selected.bg, color: selected.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 500, flexShrink: 0 }}>
                      {selected.initials}
                    </div>
                    <span>{filterUser}</span>
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: 10, color: "#888", transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </div>
                  {dropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, padding: "6px 0" }}>
                      {userOptions.map((opt, i) => (
                        <div key={opt.label}>
                          {i === 1 && <hr style={{ margin: "4px 14px", border: "none", borderTop: "0.5px solid #eee" }} />}
                          <div
                            onClick={() => { setFilterUser(opt.label); setDropdownOpen(false); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, background: filterUser === opt.label ? "#f5f5f5" : "transparent" }}
                          >
                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: opt.bg, color: opt.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 500 }}>
                              {opt.initials}
                            </div>
                            <span>{opt.label}</span>
                            {filterUser === opt.label && <span style={{ marginLeft: "auto", color: "#1a73e8", fontSize: 13 }}>✓</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── SUMMARY CARDS (Paid + Unpaid + Overdue = Total) ── */}
              <div className="px-3 pt-3 d-flex" style={{ gap: "12px", flexWrap: "wrap" }}>
                <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "12px 18px", background: "#e6f4ea", minWidth: "140px" }}>
                  <div style={{ fontSize: "12px", color: "#137333" }}>Paid</div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#137333" }}>₹ {paidTotal.toLocaleString()}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", fontSize: "18px", color: "#aaa" }}>+</div>
                <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "12px 18px", background: "#e8f0fe", minWidth: "140px" }}>
                  <div style={{ fontSize: "12px", color: "#1a73e8" }}>Unpaid</div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#1a73e8" }}>₹ {unpaidTotal.toLocaleString()}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", fontSize: "18px", color: "#aaa" }}>+</div>
                <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "12px 18px", background: "#fdecea", minWidth: "140px" }}>
                  <div style={{ fontSize: "12px", color: "#d93025" }}>Overdue</div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#d93025" }}>₹ {overdueTotal.toLocaleString()}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", fontSize: "18px", color: "#aaa" }}>=</div>
                <div style={{ border: "1px solid #e0e0e0", borderRadius: "10px", padding: "12px 18px", background: "#fff8e1", minWidth: "140px" }}>
                  <div style={{ fontSize: "12px", color: "#e65100" }}>Total</div>
                  <div style={{ fontSize: "18px", fontWeight: "700", color: "#e65100" }}>₹ {totalPurchase.toLocaleString()}</div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-danger"></div>
                </div>
              ) : filteredBills.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ paddingTop: "30px", paddingBottom: "50px" }}>
                  <div style={{ width: "100px", height: "100px", background: "#e8f4fd", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px", boxShadow: "0 4px 16px rgba(47,128,237,0.10)" }}>
                    <i className="fa-solid fa-truck-ramp-box" style={{ fontSize: "40px", color: "#2f80ed" }}></i>
                  </div>
                  <p style={{ fontSize: "15px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>No Purchase Bills to show</p>
                  <p style={{ fontSize: "13px", color: "#aaa", marginBottom: "18px" }}>You haven't added any purchase bills yet.</p>
                  {canCreate && (
                    <button
                      className="btn btn-danger"
                      onClick={() => navigate("/addpurchase")}
                      style={{ borderRadius: "8px", fontWeight: "600", padding: "8px 24px", fontSize: "14px" }}
                    >
                      <i className="fa-solid fa-plus mr-2"></i> Add Purchase
                    </button>
                  )}
                </div>
              ) : (
                <div className="px-3 pt-2" style={{ overflowY: "auto", maxHeight: "420px" }}>
                  <table className="table table-hover table-bordered table-sm invoice-table" style={{ fontSize: "13px" }}>
                    <thead style={{ background: "#222", color: "#fff" }}>
                      <tr>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Date</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Supplier Bill No.</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Supplier Name</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Payment Type</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Due Date</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Amount (₹)</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Balance (₹)</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Status</th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "10px 12px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBills.map((b, idx) => {
                        const dueInfo = !b.isCash
                          ? getDueDateInfo(b.dueDate, b.balanceAmount > 0)
                          : null;
                        return (
                          <tr key={idx}>
                            <td>{b.billDate}</td>
                            <td>{b.supplierBillNumber || "-"}</td>
                            <td>{b.supplierName}</td>
                            <td>{b.isCash ? "Cash" : "Credit"}</td>
                            <td>
                              {dueInfo ? (
                                <div>
                                  <div style={{ color: "#333" }}>{dueInfo.dateLabel}</div>
                                  {dueInfo.subText && (
                                    <div style={{ fontSize: "11px", fontWeight: "600", color: dueInfo.color }}>
                                      {dueInfo.subText}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "#aaa" }}>-</span>
                              )}
                            </td>
                            <td>₹ {b.grandTotal?.toLocaleString()}</td>
                            <td style={{ color: b.balanceAmount > 0 ? "#e74c3c" : "#27ae60" }}>
                              ₹ {b.balanceAmount?.toLocaleString()}
                            </td>
                            <td>
                              <span style={{
                                padding: "2px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "600",
                                background: b.balanceAmount > 0 ? "#fdecea" : "#e6f4ea",
                                color: b.balanceAmount > 0 ? "#d93025" : "#137333"
                              }}>
                                {b.balanceAmount > 0 ? (dueInfo?.subText?.startsWith("Overdue") ? "Overdue" : "Unpaid") : "Paid"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex" style={{ gap: "6px" }}>
                                <button
                                  className="btn btn-sm btn-light"
                                  title="View"
                                  onClick={() => handleView(b.id)}
                                >
                                  <i className="fa-solid fa-eye" style={{ color: "#2f80ed" }}></i>
                                </button>

                                {canEdit && (
                                  <button
                                    className="btn btn-sm btn-light"
                                    title="Edit"
                                    onClick={() => navigate(`/editpurchase/${b.id}`)}
                                  >
                                    <i className="fa-solid fa-pen-to-square" style={{ color: "#f39c12" }}></i>
                                  </button>
                                )}

                                {canDelete && (
                                  <button
                                    className="btn btn-sm btn-light"
                                    title="Delete"
                                    onClick={() => handleDelete(b.id)}
                                  >
                                    <i className="fa-solid fa-trash" style={{ color: "#e74c3c" }}></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ── VIEW MODAL ── */}
      {showViewModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowViewModal(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "10px", width: "600px",
              maxWidth: "95%", maxHeight: "85vh", overflowY: "auto",
              padding: "20px", position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowViewModal(false)}
              style={{
                position: "absolute", top: 14, right: 14, background: "none",
                border: "none", fontSize: 18, color: "#888", cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            {viewLoading || !viewBill ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger"></div>
              </div>
            ) : (
              <div>
                <div className="d-flex justify-content-between align-items-start mb-2 pb-2" style={{ borderBottom: "1px solid #eee" }}>
                  <div>
                    <h5 className="mb-0" style={{ fontWeight: 700, color: "#d93025" }}>PURCHASE BILL</h5>
                    <p className="mb-0" style={{ fontSize: 13 }}>{viewBill.supplierBillNumber ? `Bill #${viewBill.supplierBillNumber}` : ""}</p>
                    <p className="mb-0" style={{ fontSize: 12, color: "#777" }}>{viewBill.billDate}</p>
                  </div>
                </div>

                <div className="mb-2">
                  <p style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>SUPPLIER</p>
                  <p style={{ fontWeight: 600, marginBottom: 2 }}>{viewBill.supplierName}</p>
                  {viewBill.supplierPhone && <p style={{ fontSize: 13, color: "#555" }}>{viewBill.supplierPhone}</p>}
                </div>

                <div className="mb-2 d-flex" style={{ gap: 16, fontSize: 13 }}>
                  <span>
                    Payment: <strong>{viewBill.isCash ? "Cash" : "Credit"}</strong>
                  </span>
                  {(() => {
                    if (viewBill.isCash) return null;
                    const info = getDueDateInfo(viewBill.dueDate, viewBill.balanceAmount > 0);
                    if (!info) return null;
                    return (
                      <span>
                        Due Date: <strong>{info.dateLabel}</strong>
                        {info.subText && (
                          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: info.color }}>
                            ({info.subText})
                          </span>
                        )}
                      </span>
                    );
                  })()}
                  {viewBill.stateOfSupply && (
                    <span>State of Supply: <strong>{viewBill.stateOfSupply}</strong></span>
                  )}
                </div>

                <table className="table table-sm table-bordered" style={{ fontSize: 12, marginBottom: 8 }}>
                  <thead style={{ background: "#f5f5f5" }}>
                    <tr>
                      <th style={{ padding: "4px 8px" }}>Item</th>
                      <th style={{ padding: "4px 8px" }}>Qty</th>
                      <th style={{ padding: "4px 8px" }}>Price</th>
                      <th style={{ padding: "4px 8px" }}>Disc</th>
                      <th style={{ padding: "4px 8px" }}>Tax</th>
                      <th style={{ padding: "4px 8px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewItems?.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: "4px 8px" }}>{item.itemName}</td>
                        <td style={{ padding: "4px 8px" }}>{item.qty} {item.unit}</td>
                        <td style={{ padding: "4px 8px" }}>₹{fmt(item.pricePerUnit)}</td>
                        <td style={{ padding: "4px 8px" }}>{item.discountPercent || 0}%</td>
                        <td style={{ padding: "4px 8px" }}>{item.taxPercent || 0}%</td>
                        <td style={{ padding: "4px 8px" }}>₹{fmt(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="d-flex justify-content-end">
                  <table style={{ fontSize: 13, minWidth: 220 }}>
                    <tbody>
                      <tr style={{ borderTop: "1px solid #ddd" }}>
                        <td style={{ fontWeight: 700 }} className="pr-3">Grand Total</td>
                        <td className="text-right" style={{ fontWeight: 700 }}>₹{fmt(viewBill.grandTotal)}</td>
                      </tr>
                      <tr><td className="text-muted pr-3">Paid</td><td className="text-right">₹{fmt(viewBill.amountPaid)}</td></tr>
                      <tr>
                        <td className="text-muted pr-3">Balance</td>
                        <td className="text-right" style={{ color: viewBill.balanceAmount > 0 ? "#e74c3c" : "#27ae60", fontWeight: 600 }}>
                          ₹{fmt(viewBill.balanceAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-2 pt-2" style={{ borderTop: "1px solid #f0f0f0", fontSize: 11, color: "#aaa" }}>
                  Created by {viewBill.createdBy}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewPurchaseBills;